import { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Descriptions, Popconfirm, Space, Spin, Tag, message } from 'antd';
import { CommerceNoticeStatusCode } from '@gooduncles/gu-app-schema';

import { commerceNoticeStatusColor, commerceNoticeStatusLabel } from 'src/constants/commerce-notice.constants';
import { useCommerceNoticeById } from 'src/hooks/useCommerceNotice';
import { deleteCommerceNotice, updateCommerceNotice } from 'src/utils/firebase/firebase-short-cut';

import CommerceNoticeDetail from 'src/components/CommerceNotice/CommerceNoticeDetail/CommerceNoticeDetail';

const CommerceNoticeDetailPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { notice, isLoading } = useCommerceNoticeById(id);

  const handleDelete = async () => {
    if (!id) return;
    try {
      await deleteCommerceNotice(id);
      message.success('공지사항이 삭제되었습니다.');
      navigate('/commerce-notice/list');
    } catch (error) {
      console.error('Delete failed:', error);
      message.error('삭제에 실패했습니다.');
    }
  };

  const handleToggleVisibility = async () => {
    if (!id || !notice) return;
    const newStatus =
      notice.status === CommerceNoticeStatusCode.PUBLISHED
        ? CommerceNoticeStatusCode.HIDDEN
        : CommerceNoticeStatusCode.PUBLISHED;
    try {
      await updateCommerceNotice(id, {
        status: newStatus,
        ...(newStatus === CommerceNoticeStatusCode.PUBLISHED && !notice.publishedAt
          ? { publishedAt: new Date().toISOString() }
          : {}),
      });
      message.success(
        newStatus === CommerceNoticeStatusCode.HIDDEN
          ? '공지사항이 숨김 처리되었습니다.'
          : '공지사항이 게시되었습니다.',
      );
    } catch (error) {
      console.error('Status update failed:', error);
      message.error('상태 변경에 실패했습니다.');
    }
  };

  if (isLoading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size='large' />
      </div>
    );
  }

  if (!notice) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <p>공지사항을 찾을 수 없습니다.</p>
        <Button onClick={() => navigate('/commerce-notice/list')}>목록으로</Button>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Descriptions
        title={notice.title}
        bordered
        column={2}
        style={{ marginBottom: 24 }}
        extra={
          <Space>
            <Button onClick={() => navigate('/commerce-notice/list')}>목록</Button>
            <Button onClick={() => navigate(`/commerce-notice/edit/${id}`)}>수정</Button>
            <Button onClick={handleToggleVisibility}>
              {notice.status === CommerceNoticeStatusCode.PUBLISHED ? '숨김' : '게시'}
            </Button>
            <Popconfirm title='정말 삭제하시겠습니까?' onConfirm={handleDelete} okText='삭제' cancelText='취소'>
              <Button danger>삭제</Button>
            </Popconfirm>
          </Space>
        }
      >
        <Descriptions.Item label='상태'>
          <Tag color={commerceNoticeStatusColor[notice.status]}>{commerceNoticeStatusLabel[notice.status]}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label='카테고리'>{notice.category || '-'}</Descriptions.Item>
        <Descriptions.Item label='작성자'>{notice.authorName}</Descriptions.Item>
        <Descriptions.Item label='고정'>{notice.pinned ? '예' : '아니오'}</Descriptions.Item>
        <Descriptions.Item label='게시일'>
          {notice.publishedAt ? new Date(notice.publishedAt).toLocaleString('ko-KR') : '-'}
        </Descriptions.Item>
        <Descriptions.Item label='조회수'>{notice.viewCount}</Descriptions.Item>
      </Descriptions>

      <CommerceNoticeDetail notice={notice} />
    </div>
  );
};

export default CommerceNoticeDetailPage;
